import { useNotificationProvider } from '@refinedev/antd';
import type { NotificationProvider, OpenNotificationParams } from '@refinedev/core';

/** What a load with no answer says, once, in plain words (never "Network Error" / "Error (status code: 500)"). */
export const NO_ANSWER_TOAST = 'No answer. Check the signal and try again.';
const NO_ANSWER_KEY = 'hub-no-answer';

/** A data hook's failure that is the network, not the server: no answer, timed out, or the phone offline. */
export const isNetworkNotice = (params: OpenNotificationParams) =>
  params.type === 'error' &&
  /network error|timeout|ERR_NETWORK|Failed to fetch/i.test(
    `${params.message ?? ''} ${params.description ?? ''}`,
  );

/** What a data hook's server failure says, once, in plain words (never "Something went wrong. Please try again later"). */
export const SYSTEM_PROBLEM_TOAST = 'System problem. Try again in a moment.';
const SYSTEM_PROBLEM_KEY = 'hub-system-problem';

/** A data hook's failure that is the server (a 5xx, or the client's generic 500 words), not the network. */
export const isSystemNotice = (params: OpenNotificationParams) =>
  params.type === 'error' &&
  !isNetworkNotice(params) &&
  (/status code: 5\d\d/i.test(`${params.message ?? ''}`) ||
    /Something went wrong\. Please try again later/i.test(
      `${params.message ?? ''} ${params.description ?? ''}`,
    ));

/**
 * Refine's notifications for Hub (round 14, Imran: a reload with no signal showed a raw "Network Error (status code:
 * 500)"): a data hook that got no answer says so in plain words, one toast however many reads failed. Every other
 * notification is unchanged.
 */
export const useHubNotificationProvider = (): NotificationProvider => {
  const base = useNotificationProvider();
  return {
    ...base,
    open: params =>
      base.open(
        isNetworkNotice(params)
          ? { ...params, key: NO_ANSWER_KEY, message: NO_ANSWER_TOAST, description: undefined }
          : // Round 37, Sunita: a 500 showed "Something went wrong. Please try again later".
            isSystemNotice(params)
            ? {
                ...params,
                key: SYSTEM_PROBLEM_KEY,
                message: SYSTEM_PROBLEM_TOAST,
                description: undefined,
              }
            : params,
      ),
  };
};
